import React, { JSX, useEffect, useRef, useState } from 'react'
import { BoltIcon, ArrowUturnLeftIcon, MegaphoneIcon } from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { ConfirmDialog } from 'components/ConfirmDialog'
import { graphqlRequest } from 'lib/helpers'
import { MerchOverlaySummary, MerchTestAction, MerchTestResult } from 'types/api'
import setMerchTestModeMutation from 'graphql/setMerchTestMode.graphql'
import runMerchTestMutation from 'graphql/runMerchTest.graphql'

export const merchTestModeOf = (overlay: MerchOverlaySummary): boolean =>
  overlay.merchTestMode === true

export const merchTestScriptOf = (overlay: MerchOverlaySummary): boolean =>
  overlay.merchTestScript === true

export const SCRIPTED_STATE_COUNT = 5

export const merchTestStateOf = (overlay: MerchOverlaySummary): number => {
  const state = overlay.merchTestState
  return typeof state === 'number' && Number.isInteger(state) && state >= 1 && state <= SCRIPTED_STATE_COUNT ? state : 0
}

const nextStateOf = (overlay: MerchOverlaySummary): number =>
  (merchTestStateOf(overlay) % SCRIPTED_STATE_COUNT) + 1

export const MERCH_LIVE_CONFIRM_REFUSAL = 'This board is in a stream that is live now. Type TEST to turn on test mode.'
export const MERCH_LIVE_CONFIRM_TITLE = 'This board is in a live stream'
export const MERCH_LIVE_CONFIRM_BODY = 'Test mode changes this board on air right away, everywhere it shows. Type TEST to turn it on.'
export const MERCH_LIVE_CONFIRM_ON = 'Turn on test mode'
export const MERCH_LIVE_CONFIRM_CANCEL = 'Cancel'

export const isMerchLiveConfirmRefusal = (message: string | null | undefined): boolean =>
  message === MERCH_LIVE_CONFIRM_REFUSAL

export const MerchLiveConfirmDialog = ({
  typed,
  error,
  busy,
  onTyped,
  onConfirm,
  onCancel
}: {
  typed: string
  error: string | null
  busy: boolean
  onTyped: (value: string) => void
  onConfirm: () => void
  onCancel: () => void
}): JSX.Element => (
  <ConfirmDialog
    title={MERCH_LIVE_CONFIRM_TITLE}
    confirmLabel={MERCH_LIVE_CONFIRM_ON}
    cancelLabel={MERCH_LIVE_CONFIRM_CANCEL}
    onConfirm={() => { if (!busy) onConfirm() }}
    onCancel={onCancel}
    testId='merch-live-confirm'
  >
    <p>{MERCH_LIVE_CONFIRM_BODY}</p>
    <input
      type='text'
      value={typed}
      onChange={(e) => onTyped(e.target.value)}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        if (!busy) onConfirm()
      }}
      autoComplete='off'
      spellCheck={false}
      aria-label='Type TEST'
      data-testid='merch-live-confirm-input'
      className='mt-3 w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand'
    />
    {busy && (
      <p className='mt-2 flex items-center gap-2 text-[11px] text-dm-text-accent'>
        <LoadingSpinner />
      </p>
    )}
    {error != null && (
      <p className='mt-2 text-[11px] text-red-300' data-testid='merch-live-confirm-error'>{error}</p>
    )}
  </ConfirmDialog>
)

const CONFIRM_ENABLE_COPY = (name: string): string =>
  `Turn on test mode for "${name}"?\n\n` +
  'WARNING: TEST clicks publish simulated price changes to EVERY live surface of this board: the Twitch extension, browser sources, composites and one-off overlay pages. If this board is on air somewhere right now, viewers will see the test prices.\n\n' +
  'Your saved prices are never changed: RESET (or turning test mode back off) repaints the board at its real values.\n\n' +
  'Turn test mode on?'

export const MerchTestModeControls = ({
  board,
  auth0Token,
  onBoardChanged,
  showHints = false
}: {
  board: MerchOverlaySummary
  auth0Token: string | undefined
  onBoardChanged: (overlay: MerchOverlaySummary) => void
  showHints?: boolean
}): JSX.Element => {
  const [busy, setBusy] = useState<'toggle' | 'submode' | 'test' | 'reset' | 'broadcast' | number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [liveConfirm, setLiveConfirm] = useState<{ typed: string, error: string | null } | null>(null)
  const [autoAdvance, setAutoAdvance] = useState(false)
  const [autoIntervalS, setAutoIntervalS] = useState(10)
  const [autoCountdown, setAutoCountdown] = useState(10)

  const enabled = merchTestModeOf(board)
  const scripted = merchTestScriptOf(board)
  const currentState = merchTestStateOf(board)
  const nextState = nextStateOf(board)
  const shopBound = typeof board.shopDomain === 'string' && board.shopDomain !== ''

  const busyRef = useRef(busy)
  busyRef.current = busy

  const setTestMode = async (nextEnabled: boolean, nextScripted: boolean, busyKey: 'toggle' | 'submode', confirmLive?: string): Promise<void> => {
    if (auth0Token === undefined || busy !== null) return
    setBusy(busyKey)
    setError(null)
    try {
      const { overlay } = await graphqlRequest<{ overlay: { merchOverlayId: string, merchTestMode: boolean | null, merchTestScript: boolean | null, merchTestState: number | null } }>(
        setMerchTestModeMutation,
        {
          merchOverlayId: board.merchOverlayId,
          enabled: nextEnabled,
          scripted: nextScripted,
          ...(confirmLive != null && { confirmLive })
        },
        auth0Token
      )
      setLiveConfirm(null)
      onBoardChanged({
        ...board,
        merchTestMode: overlay.merchTestMode === true,
        merchTestScript: overlay.merchTestScript === true,
        merchTestState: overlay.merchTestState ?? 0
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to change test mode'
      if (nextEnabled && !enabled && isMerchLiveConfirmRefusal(message)) {
        setLiveConfirm((prev) => ({ typed: prev?.typed ?? '', error: confirmLive == null ? null : message }))
      } else {
        setError(message)
      }
    } finally {
      setBusy(null)
    }
  }

  const handleToggle = async (): Promise<void> => {
    if (!enabled && !globalThis.confirm(CONFIRM_ENABLE_COPY(board.name))) return
    await setTestMode(!enabled, false, 'toggle')
  }

  const handleSubMode = async (nextScripted: boolean): Promise<void> => {
    if (nextScripted === scripted) return
    await setTestMode(true, nextScripted, 'submode')
  }

  const runTest = async (
    variables: { action: MerchTestAction, state?: number, broadcast?: boolean },
    busyKey: 'test' | 'reset' | 'broadcast' | number
  ): Promise<void> => {
    if (auth0Token === undefined || busy !== null) return
    setBusy(busyKey)
    setError(null)
    try {
      const { result } = await graphqlRequest<{ result: MerchTestResult }>(
        runMerchTestMutation,
        { merchOverlayId: board.merchOverlayId, ...variables },
        auth0Token
      )
      if (typeof result.state === 'number') {
        onBoardChanged({ ...board, merchTestState: result.state })
      }
      const failed = (result.boards ?? []).filter((outcome) => !outcome.ok)
      if (failed.length > 0) {
        setError(
          'Some surfaces did not advance: ' +
          failed.map((outcome) => outcome.error ?? 'failed').join(' · ')
        )
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger the test')
    } finally {
      setBusy(null)
    }
  }

  const fireAutoRef = useRef<() => void>(() => {})
  fireAutoRef.current = () => {
    void runTest({ action: 'TEST' }, 'test')
  }
  useEffect(() => {
    if (!autoAdvance) return
    setAutoCountdown(autoIntervalS)
    const timer = globalThis.setInterval(() => {
      setAutoCountdown((s) => {
        if (s > 1) return s - 1
        if (busyRef.current !== null) return s
        fireAutoRef.current()
        return autoIntervalS
      })
    }, 1000)
    return () => globalThis.clearInterval(timer)
  }, [autoAdvance, autoIntervalS])
  useEffect(() => {
    if (error !== null) setAutoAdvance(false)
  }, [error])
  useEffect(() => {
    if (!enabled || !scripted) setAutoAdvance(false)
  }, [enabled, scripted])

  return (
    <div data-testid='merch-test-mode-block'>
      {liveConfirm != null && (
        <MerchLiveConfirmDialog
          typed={liveConfirm.typed}
          error={liveConfirm.error}
          busy={busy != null}
          onTyped={(typed) => setLiveConfirm((prev) => (prev == null ? prev : { ...prev, typed }))}
          onConfirm={() => { void setTestMode(true, false, 'toggle', liveConfirm.typed) }}
          onCancel={() => setLiveConfirm(null)}
        />
      )}
      {error !== null && (
        <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>
      )}
      <div className='flex items-center gap-3 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2'>
        <button
          type='button'
          role='switch'
          aria-checked={enabled}
          aria-label='Test mode'
          data-testid='merch-test-mode-switch'
          disabled={busy !== null || auth0Token === undefined}
          onClick={() => { void handleToggle() }}
          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60 ${enabled ? 'bg-brand' : 'bg-white/15'}`}
        >
          <span
            aria-hidden='true'
            className={`inline-block size-4 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`}
          />
        </button>
        <span className='text-sm text-dm-text-primary'>
          {enabled ? 'Test mode is on' : 'Test mode is off'}
        </span>
        {(busy === 'toggle' || busy === 'submode') && <LoadingSpinner />}
      </div>
      {enabled && (
        <div className='mt-2 flex items-center gap-2' data-testid='merch-test-submode'>
          <span className='text-[11px] uppercase tracking-wider text-dm-text-accent'>Moves</span>
          <div className='inline-flex overflow-hidden rounded-md border border-white/10'>
            <button
              type='button'
              data-testid='merch-test-submode-random'
              disabled={busy !== null || auth0Token === undefined}
              onClick={() => { void handleSubMode(false) }}
              className={`px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50 ${!scripted ? 'bg-brand text-black' : 'bg-transparent text-dm-text-secondary hover:text-dm-text-primary'}`}
            >
              Random
            </button>
            <button
              type='button'
              data-testid='merch-test-submode-scripted'
              disabled={busy !== null || auth0Token === undefined}
              onClick={() => { void handleSubMode(true) }}
              title='Five preprogrammed states, identical every session — each state flip is a Premiere sync point'
              className={`px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50 ${scripted ? 'bg-brand text-black' : 'bg-transparent text-dm-text-secondary hover:text-dm-text-primary'}`}
            >
              Scripted
            </button>
          </div>
        </div>
      )}
      {enabled && scripted && (
        <div
          className='mt-2 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-1.5 text-xs text-dm-text-primary'
          data-testid='merch-test-state-display'
        >
          {currentState === 0 ? 'At rest' : `State ${currentState} of ${SCRIPTED_STATE_COUNT}`}
          <span className='text-dm-text-accent'>{` · next ${nextState}`}</span>
        </div>
      )}
      {enabled && scripted && (
        <div
          className='mt-2 flex items-center gap-2 rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-1.5'
          data-testid='merch-test-auto-advance'
        >
          <button
            type='button'
            role='switch'
            aria-checked={autoAdvance}
            aria-label='Auto-advance'
            data-testid='merch-test-auto-advance-switch'
            disabled={auth0Token === undefined}
            onClick={() => setAutoAdvance((on) => !on)}
            className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60 ${autoAdvance ? 'bg-brand' : 'bg-white/15'}`}
          >
            <span
              aria-hidden='true'
              className={`inline-block size-3 rounded-full bg-white shadow transition-transform ${autoAdvance ? 'translate-x-[14px]' : 'translate-x-0.5'}`}
            />
          </button>
          <span className='text-xs text-dm-text-primary'>Auto-advance every</span>
          <input
            type='number'
            inputMode='numeric'
            min={2}
            max={3600}
            step={1}
            value={autoIntervalS}
            data-testid='merch-test-auto-advance-interval'
            onChange={(e) => {
              const parsed = Number(e.target.value)
              if (!Number.isFinite(parsed)) return
              setAutoIntervalS(Math.min(3600, Math.max(2, Math.round(parsed))))
            }}
            className='w-16 rounded-md border border-white/10 bg-transparent px-2 py-0.5 text-xs text-dm-text-primary focus:outline-none focus:ring-1 focus:ring-brand'
          />
          <span className='text-xs text-dm-text-primary'>s</span>
          <span className='text-xs text-dm-text-accent' data-testid='merch-test-auto-advance-countdown'>
            {autoAdvance ? `· state ${nextState} in ${autoCountdown}s` : '· off'}
          </span>
        </div>
      )}
      {enabled && (
        <div className='mt-2 grid grid-cols-2 gap-2'>
          <button
            type='button'
            data-testid='merch-test-trigger-button'
            onClick={() => { void runTest({ action: 'TEST' }, 'test') }}
            disabled={busy !== null || auth0Token === undefined}
            title={scripted
              ? `Advance to state ${nextState} — the same fixed move set every session, applied from rest`
              : 'Publish one simulated price change — half up, half down, reordered — to every live surface'}
            className='inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {busy === 'test' ? <LoadingSpinner /> : <BoltIcon className='size-4' aria-hidden='true' />}
            {scripted ? `Test · state ${nextState}` : 'Test'}
          </button>
          <button
            type='button'
            data-testid='merch-test-reset-button'
            onClick={() => { void runTest({ action: 'RESET' }, 'reset') }}
            disabled={busy !== null || auth0Token === undefined}
            title={scripted
              ? 'Repaint the board calm at its real (rest) values and restart the cycle at state 1'
              : 'Repaint the board calm at its real (rest) values'}
            className='inline-flex items-center justify-center gap-1.5 rounded-md bg-amber-400 px-3 py-2 text-xs font-semibold text-black hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {busy === 'reset' ? <LoadingSpinner /> : <ArrowUturnLeftIcon className='size-4' aria-hidden='true' />}
            Reset
          </button>
        </div>
      )}
      {enabled && scripted && (
        <>
          <div className='mt-2 flex items-center gap-1.5' data-testid='merch-test-jump-row'>
            <span className='text-[11px] uppercase tracking-wider text-dm-text-accent'>Jump</span>
            {Array.from({ length: SCRIPTED_STATE_COUNT }, (_, i) => i + 1).map((k) => (
              <button
                key={k}
                type='button'
                data-testid={`merch-test-jump-${k}`}
                onClick={() => { void runTest({ action: 'TEST', state: k }, k) }}
                disabled={busy !== null || auth0Token === undefined}
                title={`Apply state ${k} now (from rest). To re-take the flip INTO state ${k}, jump to ${k === 1 ? 'Reset' : `state ${k - 1}`} first, then Test.`}
                className={`inline-flex size-7 items-center justify-center rounded-md border text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${currentState === k
                  ? 'border-brand bg-brand/20 text-dm-text-primary'
                  : 'border-white/10 bg-transparent text-dm-text-secondary hover:text-dm-text-primary'}`}
              >
                {busy === k ? <LoadingSpinner /> : k}
              </button>
            ))}
          </div>
          <button
            type='button'
            data-testid='merch-test-broadcast-button'
            onClick={() => { void runTest({ action: 'TEST', broadcast: true }, 'broadcast') }}
            disabled={busy !== null || auth0Token === undefined}
            title={`Advance EVERY board and ticker of this account currently in scripted test mode to state ${nextState} — one click, all surfaces in lockstep`}
            className='mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-sky-500 px-3 py-2 text-xs font-semibold text-black hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {busy === 'broadcast' ? <LoadingSpinner /> : <MegaphoneIcon className='size-4' aria-hidden='true' />}
            {`All surfaces · state ${nextState}`}
          </button>
        </>
      )}
      {enabled && shopBound && showHints && (
        <p className='mt-2 text-[11px] text-amber-300/80'>
          This board is bound to {board.shopDomain}. While test mode is
          on, the test values own the board — a REAL price change from
          the store is suppressed (it repaints the test values, not
          yours) until test mode is off or the test window lapses.
        </p>
      )}
      {showHints && !scripted && (
        <p className='mt-2 text-[11px] text-dm-text-accent'>
          Each Test click moves every product a fresh random amount
          (0.1–9.9%) from its real price: half up in green, half down
          in red — and shuffles the ranking, so a board with
          reorder-on-change visibly flips. Items with no price (or
          under 6 cents, where no percent in range moves a whole cent)
          sit the test out; items under $5 move in coarser random
          steps, since every move is at least one cent. The test frame
          fades back to real values on its own after 30 idle minutes;
          Reset (or turning the switch off) repaints it calm
          immediately. Saved prices are never changed.
        </p>
      )}
      {showHints && scripted && (
        <p className='mt-2 text-[11px] text-dm-text-accent'>
          Scripted mode plays a fixed five-state cycle: each Test click
          applies the next state&apos;s preprogrammed percentage moves,
          always measured from the real
          resting prices — so state 3 looks exactly the same in every
          take, every session, and each flip is a cut point you can
          align on in the edit. Moves are assigned by resting price
          rank, so a board and a ticker with the same products move
          identically (the board reorders as ranks change; a ticker
          holds its order and only the numbers move). Auto-advance
          clicks Test for you on a fixed cadence while this page stays
          open — handy for hands-free takes. Recording surfaces
          separately? Jump re-takes
          from any state; &quot;All surfaces&quot; flips every
          scripted-mode board of the account together. The 30-minute
          idle fade and Reset behave as in random mode — after an idle
          fade the shown state resets to rest while the number keeps
          counting, so hit Reset before a fresh take.
        </p>
      )}
    </div>
  )
}
